import React from 'react';
import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = 'July 1, 2025';

const Privacy: React.FC = () => {
  return (
    <main className="bg-slate-50 dark:bg-slate-900">
      <div className="mx-auto max-w-4xl px-4 py-8 space-y-6">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 sm:text-3xl">Privacy Policy</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Effective date: {EFFECTIVE_DATE}</p>
        </header>

        <article className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 shadow-sm space-y-6 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
          {/* §1 */}
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">1. Introduction</h2>
            <p>
              TELLURVISION (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) operates a digital
              billboard advertising platform. This Privacy Policy explains how we collect, use,
              disclose, and protect your personal information when you use our website, mobile
              applications, and related services (collectively, the &quot;Service&quot;).
            </p>
          </section>

          {/* §2 */}
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              2. Information We Collect
            </h2>

            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-3">
              2.1 Information You Provide
            </h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong>Account information:</strong> name, email address, password, and optional
                business name when you register.
              </li>
              <li>
                <strong>Payment information:</strong> payment card details are processed and stored
                by Stripe; we do not store full card numbers on our servers.
              </li>
              <li>
                <strong>Campaign content:</strong> images, videos, copy text, and other creative
                material you upload for advertising campaigns.
              </li>
              <li>
                <strong>Communications:</strong> messages you send to us through support channels or
                feedback forms.
              </li>
            </ul>

            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-3">
              2.2 Information Collected Automatically
            </h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong>Device &amp; usage data:</strong> IP address, browser type, operating system,
                pages visited, referring URL, and timestamps.
              </li>
              <li>
                <strong>Billboard interaction data:</strong> anonymized, aggregate impression counts,
                view duration, and engagement metrics collected from billboard hardware. This data is
                privacy-safe and cannot identify individual viewers.
              </li>
              <li>
                <strong>Cookies &amp; similar technologies:</strong> we use essential cookies for
                authentication and session management. We do not use third-party advertising trackers.
              </li>
            </ul>
          </section>

          {/* §3 */}
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              3. How We Use Your Information
            </h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Provide, maintain, and improve the Service.</li>
              <li>Process campaign orders and payments.</li>
              <li>
                Generate AI-assisted creative content (text copy, campaign videos, zoning
                recommendations) at your request.
              </li>
              <li>
                Provide aggregated analytics and audience demographic insights to advertisers (all
                viewer data is anonymized).
              </li>
              <li>Send transactional emails (order confirmations, campaign status updates).</li>
              <li>
                Detect, prevent, and address fraud, abuse, security incidents, and technical issues.
              </li>
              <li>Comply with legal obligations.</li>
            </ul>
          </section>

          {/* §4 */}
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              4. Sharing &amp; Disclosure
            </h2>
            <p>We do not sell your personal information. We may share data with:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong>Service providers:</strong> Stripe (payments), OpenAI (AI creative
                features), Replicate (AI video generation), and infrastructure providers who process
                data on our behalf under contractual safeguards.
              </li>
              <li>
                <strong>Legal requirements:</strong> when required by law, subpoena, or government
                request, or to protect the rights, property, or safety of TELLURVISION, our users,
                or the public.
              </li>
              <li>
                <strong>Business transfers:</strong> in connection with a merger, acquisition, or
                sale of assets, with advance notice to affected users.
              </li>
            </ul>
          </section>

          {/* §5 */}
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">5. Data Retention</h2>
            <p>
              We retain account data for as long as your account is active. Campaign analytics and
              aggregate audience data may be retained in anonymized form indefinitely for reporting
              purposes. Payment records are retained as required by tax and accounting regulations.
              You may request account deletion at any time (see Section 7).
            </p>
          </section>

          {/* §6 */}
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">6. Security</h2>
            <p>
              We implement industry-standard security measures, including encrypted data
              transmission (TLS), hashed passwords (bcrypt), token-based authentication (JWT), and
              role-based access controls. No system is 100% secure; we cannot guarantee absolute
              security but we will promptly notify affected users of any material breach.
            </p>
          </section>

          {/* §7 */}
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">7. Your Rights</h2>
            <p>Depending on your jurisdiction, you may have the right to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Access the personal data we hold about you.</li>
              <li>Request correction of inaccurate data.</li>
              <li>Request deletion of your account and personal data.</li>
              <li>Object to or restrict certain processing activities.</li>
              <li>Receive a portable copy of your data.</li>
            </ul>
            <p>
              To exercise these rights, contact us at{' '}
              <a
                href="mailto:privacy@TELLURVISION.com"
                className="text-blue-600 underline hover:text-blue-800"
              >
                privacy@TELLURVISION.com
              </a>
              . We will respond within 30 days.
            </p>
          </section>

          {/* §8 */}
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              8. Children&apos;s Privacy
            </h2>
            <p>
              The Service is not directed to individuals under 18. We do not knowingly collect
              personal information from children. If we learn that we have inadvertently collected
              such information, we will delete it promptly.
            </p>
          </section>

          {/* §9 */}
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              9. Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy periodically. We will notify registered users via
              email or in-app notification at least 14 days before material changes take effect. The
              &quot;Effective date&quot; at the top of this page reflects the latest revision.
            </p>
          </section>

          {/* §10 */}
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">10. Contact</h2>
            <p>
              Questions about this Privacy Policy? Contact us at{' '}
              <a
                href="mailto:privacy@TELLURVISION.com"
                className="text-blue-600 underline hover:text-blue-800"
              >
                privacy@TELLURVISION.com
              </a>
              .
            </p>
          </section>
        </article>

        <div className="flex gap-3 text-sm">
          <Link
            to="/terms"
            className="text-blue-600 underline hover:text-blue-800"
          >
            Terms &amp; Conditions
          </Link>
          <Link
            to="/"
            className="text-slate-500 dark:text-slate-400 underline hover:text-slate-700"
          >
            Back to home
          </Link>
        </div>
      </div>
    </main>
  );
};

export default Privacy;
